import type { AdminUserAuthorization } from '@/api/core/users';
import { DOMAIN_LABELS } from './user-presentation';

const MASKS: Record<string, (version: number) => number> = {
  inventory: () => 31,
  booking: (v) => [3, 31, 63, 63, 127, 255, 255, 255][v - 1] ?? 0,
  user: (v) => (v >= 2 ? 7 : 3),
  property: (v) => (v >= 8 ? 7 : 3),
  order: () => 1,
  payment: () => 3,
  entitlement: (v) => (v >= 7 ? 3 : v >= 4 ? 1 : 0),
};

/** 저장된 규칙 버전에서 한 영역에 지정할 수 있는 권한 비트 전체. */
export function domainMaxMask(domain: string, version: number | null): number {
  return version ? (MASKS[domain]?.(version) ?? 0) : 0;
}

export interface AuthorizationDraft {
  roleBits: number;
  overrides: Record<string, number>;
}

export const MAX_OVERRIDES = 4;

export function draftFrom(current: AdminUserAuthorization): AuthorizationDraft {
  return { roleBits: current.role_bits, overrides: { ...current.domain_overrides } };
}

export function sameDraft(current: AdminUserAuthorization, draft: AuthorizationDraft): boolean {
  const before = current.domain_overrides;
  const keys = new Set([...Object.keys(before), ...Object.keys(draft.overrides)]);
  return (
    current.role_bits === draft.roleBits &&
    [...keys].every((key) => before[key] === draft.overrides[key])
  );
}

/** 서버가 검증하기 전에 알 수 있는 입력 오류만 막는다. 권한 허용 여부는 서버가 판단한다. */
export function validateDraft(
  current: AdminUserAuthorization,
  draft: AuthorizationDraft,
  reason: string,
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!Number.isSafeInteger(draft.roleBits) || draft.roleBits < 0 || draft.roleBits > 15)
    errors.role = '역할을 다시 확인해 주세요.';
  if (Object.keys(draft.overrides).length > MAX_OVERRIDES)
    errors.domains = `직접 지정은 최대 ${String(MAX_OVERRIDES)}개 영역까지 설정할 수 있습니다.`;
  if (!current.assigned && Object.keys(draft.overrides).length)
    errors.domains = '권한을 처음 배정할 때는 역할만 지정할 수 있습니다.';
  for (const [domain, mask] of Object.entries(draft.overrides)) {
    const max = domainMaxMask(domain, current.registry_version);
    if (max === 0 || !Number.isSafeInteger(mask) || mask < 0 || (mask & ~max) !== 0)
      errors[`domain:${domain}`] =
        `${DOMAIN_LABELS[domain] ?? '권한 영역'}의 지정 내용을 다시 확인해 주세요.`;
  }
  if (!reason.trim()) errors.reason = '변경 사유를 입력해 주세요.';
  else if (reason.length > 500) errors.reason = '변경 사유는 500자 이하여야 합니다.';
  return errors;
}
